import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytes,
  type StorageReference,
} from 'firebase/storage'
import { storage } from '../utils/firebase'
import type { NormalizedImage, NormalizedImageMimeType } from './imageProcessingService'

export type StoredImage = {
  storagePath: string
  downloadUrl: string
  mimeType: NormalizedImageMimeType
  width: number
  height: number
  byteSize: number
  hash: string
}

const ownerPrefix = (userId: string) => `users/${userId}/`
export const imageExtensionFor = (mimeType: NormalizedImageMimeType) => (
  mimeType === 'image/webp' ? 'webp' : 'jpg'
)

export const isOwnedStoragePath = (userId: string, storagePath: string) => (
  Boolean(userId) && storagePath.startsWith(ownerPrefix(userId)) && !storagePath.includes('..')
)

const requireOwnedPath = (userId: string, storagePath: string) => {
  if (!isOwnedStoragePath(userId, storagePath)) throw new Error('The image path is not owned by this account.')
}

const uploadImage = async (storagePath: string, image: NormalizedImage): Promise<StoredImage> => {
  const imageRef = ref(storage, storagePath)
  await uploadBytes(imageRef, image.blob, {
    contentType: image.mimeType,
    cacheControl: 'private,max-age=31536000,immutable',
    customMetadata: {
      width: String(image.width),
      height: String(image.height),
      byteSize: String(image.byteSize),
      hash: image.hash,
    },
  })

  let downloadUrl: string
  try {
    downloadUrl = await getDownloadURL(imageRef)
  } catch (error) {
    await deleteObject(imageRef).catch(() => undefined)
    throw error
  }

  return {
    storagePath,
    downloadUrl,
    mimeType: image.mimeType,
    width: image.width,
    height: image.height,
    byteSize: image.byteSize,
    hash: image.hash,
  }
}

const deleteReference = async (imageRef: StorageReference) => {
  try {
    await deleteObject(imageRef)
  } catch (error) {
    if ((error as { code?: string }).code !== 'storage/object-not-found') throw error
  }
}

const deleteTree = async (treeRef: StorageReference): Promise<number> => {
  const result = await listAll(treeRef)
  const nestedCounts = await Promise.all(result.prefixes.map(deleteTree))
  await Promise.all(result.items.map(deleteReference))
  return result.items.length + nestedCounts.reduce((sum, count) => sum + count, 0)
}

export const attachmentStorageService = {
  async uploadCaptureImage(userId: string, captureId: string, image: NormalizedImage) {
    const storagePath = `users/${userId}/captures/${captureId}/${image.hash}.${imageExtensionFor(image.mimeType)}`
    requireOwnedPath(userId, storagePath)
    return uploadImage(storagePath, image)
  },

  async uploadProfileImage(userId: string, image: NormalizedImage) {
    const storagePath = `users/${userId}/profile/avatar-${Date.now()}-${image.hash}.${imageExtensionFor(image.mimeType)}`
    requireOwnedPath(userId, storagePath)
    return uploadImage(storagePath, image)
  },

  async getOwnedDownloadUrl(userId: string, storagePath: string) {
    requireOwnedPath(userId, storagePath)
    return getDownloadURL(ref(storage, storagePath))
  },

  async deleteOwnedImage(userId: string, storagePath: string) {
    requireOwnedPath(userId, storagePath)
    await deleteReference(ref(storage, storagePath))
  },

  deleteCaptureImages(userId: string) {
    return deleteTree(ref(storage, `users/${userId}/captures`))
  },

  deleteAllUserImages(userId: string) {
    return deleteTree(ref(storage, `users/${userId}`))
  },
}
